import React from "react";

// Renders a fraction "n/d" as a circular pie chart
// n = numerator (slices filled), d = denominator (total slices)
export const FractionVisual = ({
  numerator,
  denominator,
  size = 64,
  color = "#EC4899",
  strokeColor = "#9D174D",
  emptyColor = "#FCE7F3",
  showLabel = false,
}) => {
  const n = Math.max(0, Math.min(numerator, denominator));
  const d = Math.max(1, denominator);
  const r = size / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;

  const slicePath = (i) => {
    if (d === 1) return null;
    const angleStep = (2 * Math.PI) / d;
    const start = i * angleStep - Math.PI / 2;
    const end = (i + 1) * angleStep - Math.PI / 2;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = angleStep > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  return (
    <span className="inline-flex flex-col items-center" aria-label={`${n} de ${d}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {d === 1 ? (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={n >= 1 ? color : emptyColor}
            stroke={strokeColor}
            strokeWidth="2"
          />
        ) : (
          <>
            {Array.from({ length: d }).map((_, i) => (
              <path
                key={i}
                d={slicePath(i)}
                fill={i < n ? color : emptyColor}
                stroke={strokeColor}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            ))}
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={strokeColor}
              strokeWidth="2"
            />
          </>
        )}
      </svg>
      {showLabel && (
        <span className="text-xs font-bold text-[#5C5C60] mt-1">
          {n}/{d}
        </span>
      )}
    </span>
  );
};

// Parse a fraction string like "3/4" or "5" into { numerator, denominator }
// Returns null if not a fraction.
export function parseFraction(str) {
  if (typeof str !== "string") return null;
  const m = str.trim().match(/^(\d+)\/(\d+)$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const d = parseInt(m[2], 10);
  if (Number.isNaN(n) || Number.isNaN(d) || d === 0) return null;
  return { numerator: n, denominator: d };
}

export default FractionVisual;
